/**
 * Recipe Collection API Client
 * Handles recipe collection/group operations
 */

import { apiRequest } from './queryClient';
import type {
  RecipeCollection,
  UpdateCollectionRequest,
  UpdateIngredientStatusDTO,
  UpdateIngredientOrderDTO,
} from '../types/api';

/**
 * Recipe Collections (Recipe Groups) API Client
 * Handles recipe collection/group operations
 */
export class RecipeCollectionClient {
  /**
   * Get all recipe collections/groups
   */
  static async getCollections(): Promise<RecipeCollection[]> {
    const response = await apiRequest('GET', '/recipe-groups');
    return response.json();
  }

  /**
   * Get a specific collection by ID
   */
  static async getCollection(id: string): Promise<RecipeCollection> {
    const response = await apiRequest('GET', `/recipe-groups/${id}`);
    return response.json();
  }

  /**
   * Create a new collection
   */
  static async createCollection(title: string): Promise<RecipeCollection> {
    const response = await apiRequest('POST', '/recipe-groups', { title });
    return response.json();
  }

  /**
   * Update a collection
   */
  static async updateCollection(id: string, data: UpdateCollectionRequest): Promise<RecipeCollection> {
    const response = await apiRequest('PUT', `/recipe-groups/${id}`, data);
    return response.json();
  }

  /**
   * Delete a collection
   */
  static async deleteCollection(id: string): Promise<void> {
    await apiRequest('DELETE', `/recipe-groups/${id}`);
  }

  /**
   * Add a recipe to a collection
   */
  static async addRecipeToCollection(
    collectionId: string, 
    recipeId: string
  ): Promise<RecipeCollection> {
    const response = await apiRequest('POST', `/recipe-groups/${collectionId}/add-recipe`, { recipeId });
    return response.json();
  }

  /**
   * Bulk add multiple recipes to a collection (assumes backend supports this endpoint)
   */
  static async addRecipesToCollection(
    collectionId: string,
    recipeIds: string[]
  ): Promise<RecipeCollection> {
    const response = await apiRequest('POST', `/recipe-groups/${collectionId}/add-recipes`, { recipeIds });
    return response.json();
  }

  /**
   * Remove a recipe from a collection
   */
  static async removeRecipeFromCollection(
    collectionId: string, 
    recipeId: string
  ): Promise<void> {
    await apiRequest('DELETE', `/recipe-groups/${collectionId}/delete-recipe`, { recipeId });
  }

  /**
   * Generate or regenerate the shopping list ingredients for a collection
   */
  static async generateIngredients(
    collectionId: string
  ): Promise<RecipeCollection> {
    const response = await apiRequest('POST', `/recipe-groups/${collectionId}/generate-ingredients`, {});
    return response.json();
  }

  /**
   * Update ingredient status by ID in a collection (for shopping lists)
   */
  static async updateIngredientStatusById(
    collectionId: string,
    ingredientId: string,
    data: UpdateIngredientStatusDTO
  ): Promise<RecipeCollection> {
    const response = await apiRequest('PUT', `/recipe-groups/${collectionId}/ingredients/${ingredientId}`, data);
    return response.json();
  }

  /**
   * Reorder ingredients in a collection shopping list
   */
  static async reorderIngredients(
    collectionId: string,
    data: UpdateIngredientOrderDTO
  ): Promise<RecipeCollection> {
    const response = await apiRequest('PUT', `/recipe-groups/${collectionId}/ingredients/reorder`, data);
    return response.json();
  }
}


